import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { FormInstance } from 'antd';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './EditorForm.scss';
import QuillResize from 'quill-image-resize-module-react';
import { useTranslation } from 'react-i18next';

Quill.register('modules/resize', QuillResize);

interface EditorFormProps {
  form: FormInstance;
  name?: string;
  mergeFields: MergeField[];
  height?: number;
  open?: boolean;
  hasEmailTemplatesWrite?: boolean;
}

interface MergeField {
  label: string;
  value: string;
  key: string;
  sample: string;
}

type RangeStatic = {
  index: number;
  length: number;
};

const EditorForm: React.FC<EditorFormProps> = ({ form, name = 'messageContent', mergeFields, height, open, hasEmailTemplatesWrite }) => {
  const quillRef = useRef<ReactQuill | null>(null);
  const lastRange = useRef<RangeStatic | null>(null);
  const value = form.getFieldValue(name) || '';

  const { t } = useTranslation();
  
  const imageHandler = useCallback(() => {
    if (!hasEmailTemplatesWrite) return;
    const url = prompt(t('emailTemplates.form.enterImageUrl'));
    const quill = quillRef.current?.getEditor();
    const range = quill?.getSelection();
    if (quill && range && url) {
      quill.clipboard.dangerouslyPasteHTML(
        range.index,
        `<img src="${url}" style="max-width: 100%; height: auto;" />`
      );
    }
  }, [hasEmailTemplatesWrite, t]);

  const createInsertFieldDropdown = useCallback(() => {
    const select = document.createElement('select');
    select.className = 'ql-insertField';

    const defaultOption = new Option(t('emailTemplates.form.insertField'), '');
    select.appendChild(defaultOption);

    mergeFields.forEach(({ label, value }) => {
      select.appendChild(new Option(label, value));
    });

    select.onchange = (e) => {
      if (!hasEmailTemplatesWrite) {
        select.value = '';
        return;
      }
      const val = (e.target as HTMLSelectElement).value;
      if (!val) return;
      const quill = quillRef.current?.getEditor();
      const range = lastRange.current || quill?.getSelection();
      if (!quill) return;
      quill.insertText(range?.index ?? quill.getLength(), val);
      quill.setSelection((range?.index ?? 0) + val.length, 0);
      select.value = '';
    };

    return select;
  }, [mergeFields, hasEmailTemplatesWrite, t]);

  const initializeInsertFieldDropdown = useCallback(() => {
    const toolbar = document.querySelector('.ql-toolbar');
    if (!toolbar) return;

    toolbar.querySelector('.ql-insertField')?.parentElement?.remove();

    const wrapper = document.createElement('span');
    wrapper.className = 'ql-formats';
    wrapper.appendChild(createInsertFieldDropdown());
    toolbar.appendChild(wrapper);
  }, [createInsertFieldDropdown]);

  useEffect(() => {
    const timer = setTimeout(initializeInsertFieldDropdown, 0);
    return () => clearTimeout(timer);
  }, [mergeFields, open, initializeInsertFieldDropdown]);

  const modules = useMemo(() => {
    const TOOLBAR_OPTIONS = [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline'],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      ['clean'],
    ];
    const base = {
      toolbar: {
        container: TOOLBAR_OPTIONS,
        handlers: { image: imageHandler },
      },
      clipboard: {
        matchVisual: false,
      },
    };
    if (hasEmailTemplatesWrite) {
      return {
        ...base,
        resize: {
          modules: ['Resize'],
          keyboardSelect: true,
          parchment: {
            image: {
              attribute: ['width', 'height'],
              limit: { minWidth: 20 },
              ratio: true,
            },
          },
        },
      };
    }
    return base;
  }, [imageHandler, hasEmailTemplatesWrite]);

  (window as any).Quill = Quill //Handles quill image deletion error

  return (
    <div className="quill-wrapper">
      <ReactQuill
        ref={quillRef}
        value={value}
        onChange={(val) =>
          form.setFieldsValue({ [name]: (val) })
        }
        preserveWhitespace={false}
        modules={modules}
        style={{ backgroundColor: 'white', height: height, marginBottom: 30 }}
        readOnly={!hasEmailTemplatesWrite}
      />
    </div>
  );
};

export default EditorForm;
